import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2 } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CustomDomain {
  _id: string;
  hostname: string;
  createdAt?: string;
}

export function Domains() {
  const [customDomains, setCustomDomains] = useState<CustomDomain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hostname, setHostname] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/custom-domains");

        if (response.ok) {
          const data = await response.json();
          setCustomDomains(data.customDomains || []);
        } else {
          console.error("Failed to fetch custom domains");
        }

        setBreadcrumbs([
          { label: "Website", path: "/pages" },
          { label: "Domains", path: "/sites/domains" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  const handleAdd = () => {
    setHostname("");
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    const trimmedHostname = hostname.trim().toLowerCase();

    if (!trimmedHostname) {
      alert("Hostname is required");
      return;
    }

    const hostnameExists = customDomains.some(
      (domain) => domain.hostname.toLowerCase() === trimmedHostname
    );

    if (hostnameExists) {
      alert("A domain with this hostname already exists");
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch("/api/custom-domains", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ hostname: trimmedHostname }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create custom domain");
      }

      const data = await response.json();
      setCustomDomains([data.customDomain, ...customDomains]);
      setIsDialogOpen(false);
      setHostname("");
    } catch (error) {
      console.error("Error creating custom domain:", error);
      alert(error instanceof Error ? error.message : "Failed to create custom domain");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (domainId: string) => {
    if (!confirm("Are you sure you want to delete this domain?")) {
      return;
    }

    setDeletingId(domainId);
    try {
      const response = await fetch(`/api/custom-domains/${domainId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete custom domain");
      }

      setCustomDomains(customDomains.filter((domain) => domain._id !== domainId));
    } catch (error) {
      console.error("Error deleting custom domain:", error);
      alert(error instanceof Error ? error.message : "Failed to delete custom domain");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-end mb-4">
        <Button onClick={handleAdd}>
          Add
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Hostname</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customDomains && customDomains.length > 0 ? (
            customDomains.map((domain) => (
              <TableRow key={domain._id}>
                <TableCell>{domain.hostname}</TableCell>
                <TableCell>
                  {domain.createdAt ? formatDate(domain.createdAt) : "-"}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(domain._id)}
                    disabled={deletingId === domain._id}
                  >
                    {deletingId === domain._id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="h-24 text-center">
                No domains found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Domain</DialogTitle>
            <DialogDescription>
              Enter the hostname for your custom domain.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="hostname">Hostname</Label>
              <Input
                id="hostname"
                value={hostname}
                onChange={(e) => setHostname(e.target.value)}
                placeholder="example.com"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSave();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

